<template>
    <div class="suscription-v3-container">
        <!-- Status Banner -->
        <div
            v-if="showBanner"
            class="ppv2-mb-8 ppv2-rounded-2xl ppv2-p-6 ppv2-flex ppv2-items-start ppv2-gap-4 ppv2-relative"
            :class="bannerClass"
        >
            <div class="ppv2-flex-shrink-0 ppv2-mt-0.5">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        stroke="currentColor"
                        stroke-width="2"
                    />
                    <path d="M12 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
            </div>
            <div class="ppv2-flex-grow">
                <p class="poppinsSemiBold ppv2-text-base ppv2-mb-1">{{ bannerTitle }}</p>
                <p class="poppinsRegular ppv2-text-sm ppv2-opacity-80 mb-0">{{ bannerMessage }}</p>
            </div>
            <button
                @click="dismissBanner"
                class="ppv2-flex-shrink-0 ppv2-p-1 ppv2-rounded-full hover:ppv2-bg-white/20 ppv2-transition-all"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
            </button>
        </div>

        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Background blur effects -->
            <div class="ppv2-absolute ppv2-inset-0 ppv2-pointer-events-none">
                <div
                    class="ppv2-absolute -ppv2-top-32 -ppv2-left-32 ppv2-w-[30%] ppv2-h-[260px] ppv2-rounded-full ppv2-bg-purple-600/30 ppv2-blur-[87px]"
                ></div>
                <div
                    class="ppv2-absolute ppv2-top-0 ppv2-right-0 ppv2-w-[30%] ppv2-h-[260px] ppv2-rounded-full ppv2-bg-purple-600/30 ppv2-blur-[87px]"
                ></div>
            </div>

            <!-- Main Content -->
            <div class="ppv2-relative ppv2-z-10 md:ppv2-px-8 ppv2-pb-16 ppv2-overflow-visible">
                <!-- Hero Section -->
                <div
                    class="ppv2-text-center"
                    :style="{ marginTop: normalizeSizeVertical(40, true), marginBottom: normalizeSizeVertical(90, true) }"
                >
                    <h1 class="poppinsSemiBold ppv2-text-black ppv2-mb-4" :style="{ fontSize: normalizeFontSize(30) }">
                        {{ $t('subscription.hero.title') }}
                    </h1>
                    <p
                        class="ppv2-text-black ppv2-text-opacity-80 ppv2-mx-auto ppv2-max-w-[440px]"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        {{ $t('subscription.hero.subtitle') }}
                    </p>
                </div>

                <!-- Pricing Cards -->
                <div
                    :style="{ marginBottom: normalizeSizeVertical(90, true) }"
                    class="ppv2-max-w-7xl ppv2-mx-auto ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-4 lg:ppv2-grid-cols-4 ppv2-gap-3"
                >
                    <div
                        v-for="(plan, index) in pricingPlans"
                        :key="index"
                        :class="[
                            'ppv2-rounded-[24px] ppv2-flex ppv2-flex-col ppv2-relative ppv2-transition-all ppv2-duration-300 ppv2-overflow-hidden ppv2-shadow-xl ppv2-max-w-[350px] ppv2-mx-auto',
                            plan.highlighted ? '' : 'ppv2-bg-white dark:ppv2-bg-dark ppv2-shadow-card',
                        ]"
                        :style="{
                            backgroundImage: plan.highlighted ? 'url(/media/bo/backgounds/bgCardFacture.png)' : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            padding: normalizeSize(30, true),
                        }"
                    >
                        <!-- Content Wrapper -->
                        <div class="ppv2-relative ppv2-z-10 ppv2-flex ppv2-flex-col ppv2-h-full">
                            <!-- Badge for popular plan -->
                            <div
                                v-if="plan.key === 'pro'"
                                style="background: linear-gradient(90deg, #8241d7 0%, #3a329d 100%)"
                                class="ppv2-absolute ppv2-top-0 ppv2-right-0 ppv2-px-4 ppv2-py-1.5 ppv2-rounded-full ppv2-text-white ppv2-text-sm"
                            >
                                {{ $t('subscription.plans.' + plan.key + '.badge') }}
                            </div>

                            <!-- Plan Name -->
                            <h3
                                :class="[
                                    'ppv2-text-2xl poppinsSemiBold ppv2-mb-8',
                                    plan.highlighted ? 'ppv2-text-white' : 'ppv2-text-black',
                                ]"
                            >
                                {{ $t('subscription.plans.' + plan.key + '.name') }}
                            </h3>

                            <!-- Price -->
                            <div class="ppv2-mb-6">
                                <div
                                    :class="['poppinsMedium', plan.highlighted ? 'ppv2-text-white' : 'ppv2-text-navy-dark']"
                                    :style="{ fontSize: normalizeFontSize(60) }"
                                >
                                    {{ $t('subscription.common.' + plan.key + '_price') }}
                                    <span
                                        v-if="plan.key !== 'free'"
                                        :style="{ fontSize: normalizeFontSize(14) }"
                                        :class="[
                                            'ppv2-text-sm ppv2-mt-1',
                                            plan.highlighted ? 'ppv2-text-white/80' : 'ppv2-text-black',
                                        ]"
                                    >
                                        {{ $t('subscription.common.monthly_ht') }}
                                    </span>
                                </div>
                            </div>

                            <!-- Subtitle -->
                            <p
                                :style="{ fontSize: normalizeFontSize(14) }"
                                :class="[
                                    'poppinsSemiBold ppv2-mb-3',
                                    plan.highlighted ? 'ppv2-text-white/90' : 'ppv2-text-black',
                                ]"
                            >
                                {{ $t('subscription.plans.' + plan.key + '.subtitle') }}
                            </p>

                            <!-- Features -->
                            <ul
                                class="ppv2-space-y-4 ppv2-flex-grow !ppv2-pl-0"
                                :style="{ marginBottom: normalizeSizeVertical(10, true) }"
                            >
                                <li
                                    v-for="(feature, fIndex) in $t('subscription.plans.' + plan.key + '.features')"
                                    :key="fIndex"
                                    class="ppv2-flex ppv2-items-start ppv2-gap-3"
                                >
                                    <svg
                                        class="ppv2-flex-shrink-0 ppv2-mt-0.5"
                                        :style="{ width: normalizeSize(18, true), height: normalizeSize(18, true) }"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M8.88321 0.648438C7.28118 0.648438 5.71513 1.12349 4.38309 2.01353C3.05105 2.90357 2.01285 4.16862 1.39978 5.6487C0.786713 7.12878 0.626306 8.75742 0.938846 10.3287C1.25139 11.8999 2.02284 13.3432 3.15564 14.476C4.28845 15.6088 5.73173 16.3803 7.30298 16.6928C8.87422 17.0053 10.5029 16.8449 11.9829 16.2319C13.463 15.6188 14.7281 14.5806 15.6181 13.2486C16.5082 11.9165 16.9832 10.3505 16.9832 8.74844C16.9832 6.60018 16.1298 4.53992 14.6108 3.02087C13.0917 1.50183 11.0315 0.648438 8.88321 0.648438ZM12.6902 6.46244L8.69421 12.0064C8.57518 12.1671 8.40147 12.2787 8.20584 12.3202C8.01021 12.3616 7.80617 12.3301 7.63221 12.2314L5.33721 10.8904C5.15102 10.7783 5.01703 10.5967 4.96471 10.3857C4.91239 10.1747 4.94602 9.95162 5.05821 9.76544C5.17039 9.57926 5.35195 9.44527 5.56292 9.39294C5.7739 9.34062 5.99703 9.37425 6.18321 9.48644L7.81221 10.4584L11.4122 5.51744C11.5458 5.38359 11.722 5.30071 11.9104 5.28316C12.0987 5.26562 12.2872 5.31453 12.4432 5.4214C12.5992 5.52827 12.713 5.68637 12.7646 5.86829C12.8163 6.05022 12.8027 6.24449 12.7262 6.41744L12.6902 6.46244Z"
                                            :fill="plan.highlighted ? 'white' : '#4A3AFF'"
                                        />
                                    </svg>
                                    <span
                                        :style="{ fontSize: normalizeFontSize(14) }"
                                        :class="['poppinsMedium', plan.highlighted ? 'ppv2-text-white/90' : 'ppv2-text-black']"
                                    >
                                        {{ feature }}
                                    </span>
                                </li>
                            </ul>

                            <!-- CTA Button -->
                            <button
                                v-if="(!isSubscribed || plan.key !== currentPlanKey || canContinueFree) && plan.key !== 'free'"
                                @click="handlePlanSelection(plan)"
                                :disabled="isLoading"
                                :style="{
                                    fontSize: normalizeFontSize(16),
                                    ...(canContinueFree && plan.key === currentPlanKey
                                        ? { background: 'linear-gradient(90deg, #8241D7 0%, #3A329D 100%)', color: 'white' }
                                        : { border: '1px solid #D9DBE9' }),
                                    boxShadow:
                                        '0px 4px 6px 0px #FFFFFF66 inset, 0px -2px 2px 0px #1B235512 inset, 0px 3px 6px 0px #07006E08',
                                }"
                                :class="[
                                    'ppv2-w-full ppv2-py-3 ppv2-px-6 ppv2-rounded-xl poppinsMedium ppv2-transition-all ppv2-duration-300',
                                    (canContinueFree && plan.key === currentPlanKey) || plan.highlighted
                                        ? 'ppv2-text-white'
                                        : plan.highlighted
                                          ? 'ppv2-bg-white dark:ppv2-bg-dark ppv2-text-black'
                                          : 'ppv2-bg-gradient-button ppv2-text-black',
                                ]"
                            >
                                {{ getButtonText(plan) }}
                            </button>
                            <button
                                v-else-if="plan.key === 'free' && canContinueFree"
                                @click="handleContinueFree"
                                :disabled="isLoading"
                                :style="{ fontSize: normalizeFontSize(16), border: '1px solid #D9DBE9' }"
                                class="ppv2-w-full ppv2-py-3 ppv2-px-6 ppv2-rounded-xl poppinsMedium ppv2-bg-gradient-button ppv2-text-black ppv2-transition-all ppv2-duration-300"
                            >
                                {{ $t('subscription.common.continue_free') }}
                            </button>
                            <div
                                v-else-if="plan.key === 'free'"
                                class="ppv2-w-full ppv2-py-3 ppv2-px-6 ppv2-rounded-xl poppinsMedium ppv2-text-center ppv2-bg-gray-100 ppv2-text-gray-500"
                            >
                                {{ $t('subscription.plans.free.button') || 'Gratuit' }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Client Logos Section -->
                <div class="ppv2-max-w-7xl ppv2-mx-auto" :style="{ marginBottom: normalizeSizeVertical(90, true) }">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-justify-center ppv2-gap-8">
                        <div
                            v-for="(logo, index) in partnerLogos"
                            :key="index"
                            class="ppv2-opacity-95 hover:ppv2-opacity-100 ppv2-transition-opacity ppv2-duration-300"
                        >
                            <img
                                :src="`/media/abonnement/${logo}`"
                                alt="Partner Logo"
                                class="ppv2-h-16 ppv2-w-auto ppv2-object-contain ppv2-transition-all ppv2-duration-300 ppv2-bg-white"
                            />
                        </div>
                    </div>
                </div>

                <!-- Pricing Comparison Table -->
                <SubscriptionTable
                    :current-plan-key="currentPlanKey"
                    :is-manage-page="false"
                    :can-continue-free="canContinueFree"
                    @plan-click="handlePlanSelection"
                    @free-click="handleContinueFree"
                />

                <!-- FAQ Section -->
                <SubscriptionFaq />
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'
import normalizeSizeVertical from '../../utils/normalizeSizeVertical'
import axios from 'axios'
import { pricingPlans, partnerLogos } from './constants'
import SubscriptionTable from './SubscriptionTable.vue'
import SubscriptionFaq from './SubscriptionFaq.vue'
import normalizeFontSize from '../../utils/normalizeFontSize'
export default {
    name: 'PricingPage',
    props: {
        isSubscribed: {
            type: Boolean,
            default: false,
        },
        currentPlanKey: {
            type: String,
            default: 'free',
        },
        tenant: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        tenantId: {
            type: String,
            required: false,
            default: '',
        },
        centralDomain: {
            type: String,
            required: true,
        },
        billingTokens: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        isOnTrial: {
            type: Boolean,
            default: false,
        },
        trialEndsAt: {
            type: String,
            default: '',
        },
        // Essai sans abonnement Stripe : le plan d'essai s'active (checkout) et le Gratuit
        // devient « Continuer en gratuit » (PLPRB-2805).
        canContinueFree: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        SubscriptionTable,
        SubscriptionFaq,
    },
    data() {
        return {
            availableButton: false,
            isLoading: false,
            bannerDismissed: false,
            partnerLogos,
            pricingPlans,
            freshTokens: null,
            usedTokens: [],
        }
    },

    computed: {
        showBanner() {
            if (this.bannerDismissed) return false
            const status = (this.tenant && this.tenant.subscription_status) || ''
            return ['past_due', 'unpaid', 'incomplete'].includes(status)
        },
        bannerClass() {
            const status = (this.tenant && this.tenant.subscription_status) || ''
            if (['past_due', 'unpaid', 'incomplete'].includes(status))
                return 'ppv2-bg-red-500/10 ppv2-border ppv2-border-red-500/30 ppv2-text-red-400'
            return 'ppv2-bg-yellow-500/10 ppv2-border ppv2-border-yellow-500/30 ppv2-text-yellow-400'
        },
        bannerTitle() {
            const status = (this.tenant && this.tenant.subscription_status) || ''
            if (['past_due', 'unpaid', 'incomplete'].includes(status))
                return this.$t('subscription.banner.payment_failed_title') || 'Paiement échoué'
            return this.$t('subscription.banner.warning_title') || 'Attention'
        },
        bannerMessage() {
            const status = (this.tenant && this.tenant.subscription_status) || ''
            if (['past_due', 'unpaid', 'incomplete'].includes(status))
                return (
                    this.$t('subscription.banner.payment_failed_message') ||
                    'Le paiement de votre abonnement a échoué. Veuillez mettre à jour votre moyen de paiement pour éviter la suspension de votre compte.'
                )
            return this.$t('subscription.banner.warning_message') || 'Une action est requise sur votre abonnement.'
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,
        getPlanLevel(key) {
            const levels = { free: 0, essentiel: 1, pro: 2, premium: 3 }
            return levels[key] || 0
        },
        getButtonText(plan) {
            if (this.canContinueFree && plan.key === this.currentPlanKey) {
                return this.$t('subscription.common.activate_plan', { plan: this.$t('subscription.plans.' + plan.key + '.name') })
            }
            if (this.isSubscribed) {
                const currentLevel = this.getPlanLevel(this.currentPlanKey)
                const planLevel = this.getPlanLevel(plan.key)
                if (planLevel > currentLevel) return this.$t('subscription.common.upgrade') || 'Upgrade'
                if (planLevel < currentLevel) return this.$t('subscription.manage.downgrade') || 'Downgrade'
            }
            return this.$t('subscription.plans.' + plan.key + '.button')
        },
        resolveTenantId() {
            let tenantId = this.tenantId || (this.tenant && this.tenant.id)

            if (!tenantId && typeof this.tenant === 'string' && this.tenant.startsWith('{')) {
                try {
                    const parsedTenant = JSON.parse(this.tenant)
                    tenantId = parsedTenant.id || tenantId
                } catch (e) {
                    tenantId = null
                }
            }

            if (typeof tenantId === 'string' && tenantId.startsWith('{')) {
                try {
                    const parsed = JSON.parse(tenantId)
                    tenantId = parsed.id || parsed
                } catch (e) {
                    tenantId = null
                }
            }

            return tenantId ? String(tenantId) : null
        },
        // Decode the token's exp (base64url JSON payload before the ".") in ms, or 0.
        tokenExpiresAt(token) {
            try {
                let b64 = String(token).split('.')[0].replace(/-/g, '+').replace(/_/g, '/')
                while (b64.length % 4) b64 += '='
                const payload = JSON.parse(atob(b64))
                return payload && payload.exp ? payload.exp * 1000 : 0
            } catch (e) {
                return 0
            }
        },
        // Return a usable billing token: valid for at least 30s AND not already
        // used. Tokens are single-use server-side, so once handed out we mark it
        // and refresh from the BO endpoint on the next click (also covers expiry).
        async getValidBillingToken(name) {
            const tokens = this.freshTokens || this.billingTokens || {}
            let token = tokens[name]
            const usable = token && this.tokenExpiresAt(token) - Date.now() > 30000 && !this.usedTokens.includes(token)
            if (!usable) {
                try {
                    const { data } = await axios.get('/back-office/subscription/billing-tokens')
                    if (data && data.checkout) {
                        this.freshTokens = data
                    }
                } catch (e) {
                    // Fall back to whatever token we already have.
                }
                token = (this.freshTokens || this.billingTokens || {})[name]
            }
            if (token) {
                this.usedTokens.push(token)
            }
            return token
        },
        async handlePlanSelection(plan) {
            try {
                const tenantId = this.resolveTenantId()

                if (!tenantId) {
                    this.$toast.error(this.$t('common.tenant_not_found'))
                    return
                }

                this.isLoading = true
                console.log('Sending checkout request for plan:', plan, 'tenant:', tenantId)

                this.$toast.info(this.$t('subscription.redirecting_stripe'), { timeout: 2000 })

                const planKey = typeof plan === 'string' ? plan : plan.key
                const checkoutToken = await this.getValidBillingToken('checkout')

                const response = await axios.post(`${this.centralDomain}/saas-subscriptions/checkout`, {
                    plan_slug: planKey,
                    tenant_id: tenantId,
                    billing_token: checkoutToken,
                    success_url: window.location.origin + '/back-office/manage-subscription',
                    cancel_url: window.location.href,
                })

                if (response.data.url) {
                    window.location.href = response.data.url
                } else {
                    throw new Error('No checkout URL returned')
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.error || error.response?.data?.message || 'Une erreur est survenue')
            } finally {
                this.isLoading = false
            }
        },
        async handleContinueFree() {
            const tenantId = this.resolveTenantId()
            if (!tenantId) {
                this.$toast.error(this.$t('common.tenant_not_found'))
                return
            }
            this.isLoading = true
            try {
                const token = await this.getValidBillingToken('free')
                await axios.post(`${this.centralDomain}/saas-subscriptions/continue-free`, {
                    tenant_id: tenantId,
                    billing_token: token,
                })
                this.$toast.success(this.$t('subscription.common.continue_free_done'))
                window.location.reload()
            } catch (error) {
                this.$toast.error(error.response?.data?.error || error.response?.data?.message || 'Une erreur est survenue')
            } finally {
                this.isLoading = false
            }
        },
        dismissBanner() {
            this.bannerDismissed = true
            try {
                localStorage.setItem('subscription_banner_dismissed', 'true')
            } catch (e) {}
        },
        formatDate(date) {
            if (!date) return '-'
            return new Date(date).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            })
        },
        handleScroll() {
            const threshold = 200
            const isBeyondThreshold = window.scrollY > threshold

            if (isBeyondThreshold) {
                document.body.classList.add('pp-sticky-page')
                document.documentElement.classList.add('pp-sticky-page')
            } else {
                document.body.classList.remove('pp-sticky-page')
                document.documentElement.classList.remove('pp-sticky-page')
            }
        },
    },
    mounted() {
        try {
            if (localStorage.getItem('subscription_banner_dismissed') === 'true') {
                this.bannerDismissed = true
            }
        } catch (e) {}
        window.addEventListener('scroll', this.handleScroll)
        this.handleScroll() // Initial check
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('subscription-v3-no-bg')
        }
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.handleScroll)
        document.body.classList.remove('pp-sticky-page')
        document.documentElement.classList.remove('pp-sticky-page')
    },
}
</script>

<style scoped lang="scss">
.suscription-v3-container {
    padding: 0;
}
/* Ensure sticky works by allowing overflow on parents */
.ppv2-overflow-visible {
    overflow: visible !important;
}

/* Vue 2 transition styles */
.v-enter-active,
.v-leave-active {
    transition: all 0.3s ease;
}

.v-enter,
.v-leave-to {
    opacity: 0;
    max-height: 0 !important;
}

.v-enter-to,
.v-leave {
    opacity: 1;
}
</style>

<style>
/* Global overrides for sticky functionality on this page only */
html.pp-sticky-page,
body.pp-sticky-page,
.pp-sticky-page #page-container,
.pp-sticky-page #main-container,
.pp-sticky-page .v-application--wrap {
    overflow: visible !important;
    height: auto !important;
    min-height: 100vh;
}

#main-container:has(.subscription-v3-container) {
    background: transparent !important;
}
.dark #main-container.subscription-v3-no-bg,
.dark #main-container:has(.subscription-v3-container) {
    background: transparent !important;
}
#main-container.subscription-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}
</style>
